import { SelectAccountsPanelProps } from 'screens/BrandKit/LinkedAccountsDrawer/Types'
import Styles from './SelectAccountsPanel.module.scss'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { AccountSkeleton } from 'screens/BrandKit/LinkedAccountsDrawer/AccountSkeleton'
import { Account } from './Account'
import { Button } from 'blaze-ui/Button'
import { useCallback, useState } from 'react'
import { AccountsList } from 'screens/BrandKit/LinkedAccountsDrawer/AccountsList'
import { AccountListItem } from 'screens/BrandKit/LinkedAccountsDrawer/AccountListItem'
import { Checkbox } from 'blaze-ui/Checkbox'
import AccountStyles from './Account.module.scss'
import { useBrandKitSources } from 'hooks/queries/useBrandKitSources'
import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { collectResults } from 'utils/query'
import { CreateIntegrationPanel } from 'screens/BrandKit/LinkedAccountsDrawer/CreateIntegrationPanel'
import { Heading } from 'blaze-ui/Heading/Heading'
import { useConnectableAccounts } from 'hooks/queries/workspaces/integrationAuths/useConnectableAccounts'
import { useCreateExternalIntegration } from 'screens/OAuthRedirectV2/hooks'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { Facebook, Instagram, LinkedIn } from 'blaze-ui/icons'
import { ButtonGroup } from 'components/MobileButton/ButtonGroup'
import { MobileButton } from 'components/MobileButton/MobileButton'

export const SelectAccountsPanel = ({
  onLink,
  initialSelectedAccounts,
  brandKitId,
  integrationAuth,
  onSelectFacebook,
  onSelectInstagram,
  onSelectLinkedIn,
}: SelectAccountsPanelProps) => {
  const { mutateAsync: createExternalIntegration } = useCreateExternalIntegration()
  const workspaceId = useCurrentWorkspaceId()
  const [isLinking, setIsLinking] = useState(false)

  const linkedAccountsQuery = useBrandKitSources(
    { brandKitId, sourceType: ['ExternalIntegration'] },
    { cacheTime: 1000 * 60 * 5 },
  )
  const linkedAccounts = collectResults(linkedAccountsQuery, (data) => data.brand_kit_sources)

  const { data: externalIntegrationsData, isLoading: isLoadingIntegrations } = useExternalIntegrations({
    select: (data) => {
      const unlinkedAccounts = data.external_integrations.filter((externalIntegration) => {
        return (
          !linkedAccounts.find((linkedAccount) => linkedAccount.source.id === externalIntegration.id) &&
          externalIntegration.provider !== ExternalIntegrationEnum.WordPress
        )
      })
      return { external_integrations: unlinkedAccounts }
    },
  })
  const [selectedAccounts, setSelectedAccounts] = useState(new Set(initialSelectedAccounts || []))

  const { data: connectableAccountsData, isLoading: isLoadingConnectableAccounts } = useConnectableAccounts(
    integrationAuth?.id,
    {
      select: (data) => {
        return {
          data: data.data.filter(
            (account) =>
              !externalIntegrationsData?.external_integrations.some(
                (integration) => integration.provider_id === account.provider_id,
              ),
          ),
          metadata: data.metadata,
        }
      },
    },
  )

  const isLoading = isLoadingIntegrations || isLoadingConnectableAccounts || linkedAccountsQuery.isLoading

  const selectableAccounts = [
    ...(externalIntegrationsData?.external_integrations || []),
    ...(connectableAccountsData?.data || []),
  ]

  const handleLink = useCallback(async () => {
    setIsLinking(true)
    const integrations = await Promise.all(
      Array.from(selectedAccounts).map((account) => {
        if ('id' in account) {
          return Promise.resolve(account)
        }
        if (integrationAuth) {
          return createExternalIntegration({
            workspaceId,
            params: { ...account, integration_auth_id: integrationAuth.id },
          }).then((res) => {
            if (res.type !== SUCCESS_RESPONSE_TYPE) {
              throw new Error('Failed to link account')
            }
            return res.data.external_integration
          })
        }
        return undefined
      }),
    )

    const linkAccounts = integrations.filter((integration) => integration !== undefined) as ExternalIntegration[]
    setIsLinking(false)
    onLink(linkAccounts)
  }, [selectedAccounts, createExternalIntegration, integrationAuth, workspaceId, onLink])

  if (!isLoading && selectableAccounts.length === 0) {
    return (
      <CreateIntegrationPanel
        onSelectLinkedIn={onSelectLinkedIn}
        onSelectFacebook={onSelectFacebook}
        onSelectInstagram={onSelectInstagram}
      />
    )
  }

  return (
    <div className={Styles.root}>
      <div className={Styles.list}>
        <Heading level={5}>Select accounts to connect to this Brand Kit</Heading>
        <AccountsList>
          {isLoading &&
            Array.from({ length: 2 }).map((_, i) => (
              <AccountListItem key={i}>
                <AccountSkeleton />
              </AccountListItem>
            ))}
          {!isLoading &&
            selectableAccounts.map((account) => (
              <AccountListItem key={JSON.stringify(account)}>
                <Checkbox.Label
                  aria-label={`Select ${account.name}`}
                  className={AccountStyles.linkedAccount}
                  isSelected={selectedAccounts.has(account)}
                  onChange={(isSelected) => {
                    setSelectedAccounts((existingSelection) => {
                      const newSelection = new Set(existingSelection)
                      if (isSelected) {
                        newSelection.add(account)
                      } else {
                        newSelection.delete(account)
                      }
                      return newSelection
                    })
                  }}
                >
                  <div className={Styles.checkboxBox}>
                    <Checkbox.Box color="var(--selected-fill)" />
                  </div>
                  <Account account={account} />
                </Checkbox.Label>
              </AccountListItem>
            ))}
        </AccountsList>
        <div className={Styles.connectSocial}>
          <Heading level={4}>Connect New Account</Heading>
          <ButtonGroup>
            <MobileButton variant="tertiary" icon={Facebook} onPress={onSelectFacebook} aria-label="Connect Facebook">
              Facebook
            </MobileButton>
            <MobileButton
              variant="tertiary"
              icon={Instagram}
              onPress={onSelectInstagram}
              aria-label="Connect Instagram"
            >
              Instagram
            </MobileButton>
            <MobileButton variant="tertiary" icon={LinkedIn} onPress={onSelectLinkedIn} aria-label="Connect LinkedIn">
              LinkedIn
            </MobileButton>
          </ButtonGroup>
        </div>
      </div>
      <Button
        fullWidth
        size="lg"
        onPress={handleLink}
        isDisabled={selectedAccounts.size < 1 || isLinking}
        aria-label="Link With Brand Kit"
      >
        Link With Brand Kit
      </Button>
    </div>
  )
}
